import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { Avatar } from '~/_legacy/components/atoms/avatar/index';
import { ModalComment } from '~/_legacy/components/atoms/modal/comment';
import { CardPresentation } from '~/_legacy/components/molecules/card/presentation';
import { CardReview } from '~/_legacy/components/molecules/card/review';
import { Question } from '~/_legacy/components/molecules/question/index';
import { Footer } from '~/_legacy/components/organisms/footer';
import { Header } from '~/_legacy/components/organisms/header/Header';
import * as Models from '~/_legacy/models';

import { useSWRV1CompetitionById } from '~/api/v1/competitions/[id]/get';
import { apiDeleteV1CompetitionByIdPresentationByIdUnvote } from '~/api/v1/competitions/[id]/presentations/[id]/unvote/delete';
import { useSWRV1CompetitionByIdPresentations } from '~/api/v1/competitions/[id]/presentations/get';
import { useSWRV1PresentationById } from '~/api/v1/presentations/[id]/get';
import { ApiPresentation } from '~/api/v1/presentations/all_index/get';
import * as COLORS from '~/colors';
import { SusnetImage } from '~/components/_wrapper/SusnetImage';
import { CurrentUserState } from '~/state/currentUser';
import { Futura } from '~/styles';
import { media } from '~/utils';
import { isNotEmpty } from '~/utils/array';

import CheckedIcon from '@public/images/checked.svg';
import LinkIcon from '@public/images/link-black.svg';

type Props = {
  presentationJson: ApiPresentation;
};

export default function PresentationPage(props: Props) {
  const { id } = props.presentationJson;
  const currentUser = useRecoilValue(CurrentUserState);
  const searchParams = useSearchParams();
  const competitionId = searchParams.get('competition-id') ?? '';

  const [tab, setTab] = useState<'main' | 'comment'>('main');
  const [voteOverlay, setVoteOverlay] = useState(false);
  const [commentOverlay, setCommentOverlay] = useState(false);

  let { presentation, isLoading, mutate } = useSWRV1PresentationById({ id, competitionId });
  presentation ||= props.presentationJson;
  const { presentations } = useSWRV1CompetitionByIdPresentations(competitionId);
  const { competition } = useSWRV1CompetitionById(competitionId);

  const presentationModel = useMemo(() => {
    if (!presentation) return undefined;
    return new Models.Presentation(presentation);
  }, [presentation]);

  const fetchData = async () => {
    await mutate();
  };

  const voteCount = useMemo(() => {
    if (!presentationModel) return null;
    if (!competition) return null;
    if (!competition.vote_count_visible) return null;

    return (
      <span>
        {presentationModel.votes}
        <span>票</span>
      </span>
    );
  }, [presentationModel, competition]);

  const unvote = async () => {
    if (!competitionId) return;
    if (window.confirm('投票を取り消しますか？')) {
      await apiDeleteV1CompetitionByIdPresentationByIdUnvote({ competitionId, presentationId: id });
      await fetchData();
    }
  };

  if (!presentationModel) return <div />;

  const { organization } = presentationModel;
  const color = COLORS.sdgs[organization.sdgs?.[0] ?? 1];

  return (
    <div>
      <Header />
      {voteOverlay && (
        <ModalComment
          isVote={true}
          competitionId={competitionId}
          presentation={presentation}
          onClose={() => setVoteOverlay(false)}
          onSuccess={() => {
            fetchData();
            setVoteOverlay(false);
          }}
        />
      )}
      {commentOverlay && (
        <ModalComment
          isVote={false}
          presentation={presentation}
          onClose={() => setCommentOverlay(false)}
          onSuccess={() => {
            fetchData();
            setCommentOverlay(false);
          }}
        />
      )}
      <ButtonWrapper>
        {isLoading || !competition || competition.status !== 'voting' ? (
          // 投票中以外(団体募集時、終了時)は「投票する」ボタンを隠す
          <></>
        ) : !presentationModel.votable ? (
          <NotVotableButton
            onClick={() => {
              toast.error('該当する組織の方のみ投票できます。ログインして投票して下さい。');
            }}
          >
            <p>投票する</p>
            {voteCount}
          </NotVotableButton>
        ) : presentationModel.voted ? (
          <VotedButton onClick={unvote}>
            <CheckedIcon width='50px' />
            {voteCount}
          </VotedButton>
        ) : presentationModel.anyVoted ? (
          <AnyVotedButton onClick={() => toast.error('他団体に投票済みです。')}>
            <p>投票する</p>
            {voteCount}
          </AnyVotedButton>
        ) : (
          <Button onClick={() => setVoteOverlay(true)}>
            <p>投票する</p>
            {voteCount}
          </Button>
        )}
      </ButtonWrapper>
      <Top>
        <Video>
          <Iframe
            width='560'
            height='315'
            src={presentationModel.video}
            // frameborder='0'
            allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
            // allowfullscreen
          />
        </Video>
      </Top>
      <Names>
        <NameLeft>
          <Logos>
            <Area>{organization.area}</Area>
            <Logo src={organization.logo} />
          </Logos>
          <Labels>
            <Category>{organization.category}</Category>
            <StyledSpacer />
            <Name href={`/orgs/${organization.uid}`}>
              <span>{organization.name}</span>
              <LinkIcon />
            </Name>
            <Tags>
              {organization.tags.map((tag) => (
                <Tag key={tag.name}># {tag.name}</Tag>
              ))}
            </Tags>
            <LabelBg start={color?.start} end={color?.end} />
          </Labels>
        </NameLeft>
        {isNotEmpty(organization.sdgs) && (
          <Sdgs>
            <StyledMainImage>
              <SusnetImage
                src={`/images/sdgs/${organization.sdgs[0]}.png`}
                alt={`SDGs${organization.sdgs[0]}`}
                fill
                sizes='100vw'
              />
            </StyledMainImage>
            <div>
              {organization.sdgs.slice(1, 3).map((sdg) => (
                <StyledSubImage key={sdg}>
                  <SusnetImage
                    src={`/images/sdgs/${sdg}.png`}
                    alt={`SDGs${sdg}`}
                    fill
                    sizes='100vw'
                  />
                </StyledSubImage>
              ))}
            </div>
          </Sdgs>
        )}
      </Names>
      <Tabs>
        <Tab onClick={() => setTab('main')} active={tab === 'main'}>
          概要
        </Tab>
        <Tab onClick={() => setTab('comment')} active={tab === 'comment'}>
          コメント
          <span>{presentationModel.reviews.length}</span>
        </Tab>
      </Tabs>
      <Main>
        {tab === 'main' && (
          <>
            <Title>{presentationModel.title}</Title>
            <Description>{presentationModel.description}</Description>
            {isNotEmpty(presentationModel.links) && (
              <PresentationLinks>
                {presentationModel.links.map((link, index) => (
                  <PresentationLink target='_blank' href={link.url} key={index}>
                    {link.title}
                  </PresentationLink>
                ))}
              </PresentationLinks>
            )}
            {presentationModel.slide && (
              <SlideWrapper>
                <Slide src={presentationModel.slide} />
              </SlideWrapper>
            )}
            {presentationModel.slideVideo && (
              <SlideVideoWrapper>
                <SlideVideo
                  width='560'
                  height='315'
                  src={presentationModel.slideVideo}
                  // frameborder='0'
                  allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                  // allowfullscreen
                />
              </SlideVideoWrapper>
            )}
            {isNotEmpty(presentationModel.questions) && (
              <QuestionSection>
                <Questions>
                  {presentationModel.questions.map((q, i) => (
                    <QuestionWrapper key={i}>
                      <Question question={q} defaultOpen={true} />
                    </QuestionWrapper>
                  ))}
                </Questions>
              </QuestionSection>
            )}
          </>
        )}
        {tab === 'comment' && (
          <Comments>
            <Comment onClick={() => setCommentOverlay(true)}>
              <Avatar size={'50px'} src={currentUser && currentUser.image()} />
              <CommentText>コメントを残そう</CommentText>
            </Comment>
            {presentationModel.reviews.map((review) => (
              <CardReview
                key={review.id}
                review={review}
                onDelete={() => {
                  fetchData();
                }}
              />
            ))}
          </Comments>
        )}
        {isNotEmpty(presentations) && (
          <Section>
            <SectionTitle>
              <Futura>PRESENTATIONS</Futura>
              <span>プレゼンテーション一覧</span>
            </SectionTitle>
            <SectionContent>
              <Boxes>
                {presentations.map((p, index) => (
                  <BoxWrapper key={index}>
                    <CardPresentation presentation={p} isShowVote={true} />
                  </BoxWrapper>
                ))}
              </Boxes>
            </SectionContent>
          </Section>
        )}
      </Main>
      <Footer />
    </div>
  );
}

const ButtonWrapper = styled.div`
  position: fixed;
  right: 50px;
  bottom: 50px;
  z-index: 100;

  ${media.mid`
    right: 20px;
    bottom: 20px;
  `}
`;

const Button = styled.div`
  width: 128px;
  height: 128px;
  border-radius: 64px;
  color: #fff;
  background: linear-gradient(45deg, #0063ac, #acff9b);
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  line-height: 26px;
  letter-spacing: 1px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  & > p {
    font-size: 22px;
  }

  & > span {
    margin-top: 3px;
    font-size: 18px;
  }

  & > span > span {
    margin-left: 3px;
    font-size: 13px;
  }

  ${media.mid`
    width: 110px;
    height: 110px;
    border-radius: 55px;
    font-size: 14px;
    line-height: 24px;

    & > p {
      font-size: 20px;
    }
  `}
`;

const VotedButton = styled(Button)`
  background: linear-gradient(-45deg, #ff1f73, #ffae56);
`;

const AnyVotedButton = styled(Button)`
  background: #c0c0c0;
`;

const NotVotableButton = styled(AnyVotedButton)``;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 1650px) {
    margin-top: 85px;
  }

  ${media.mid`
    margin-top: 60px;
  `}
`;

const Video = styled.div`
  width: 1000px;
  position: relative;
  padding-top: 562.5px;

  ${media.mid`
    width: 100%;
    padding-top: 56.25%;
    object-fit: cover;
  `}
`;

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const Names = styled.div`
  margin: auto;
  height: 116px;
  width: 100%;
  max-width: 1000px;
  background-color: #f2f2f2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  ${media.mid`
    position: relative;
    flex-direction: column;
    height: auto;
  `}
`;

const NameLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${media.mid`
    width: 100%;
  `}
`;

const Logos = styled.div`
  height: 116px;
  overflow: hidden;
`;

const Area = styled.div`
  width: 85px;
  height: 31px;
  background: #000;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1px;
  text-align: center;
  line-height: 31px;
`;

const Logo = styled.img`
  width: 85px;
  height: 85px;
  object-fit: cover;
`;

const Labels = styled.div`
  padding: 7px 33px 11px;
  height: 116px;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
`;

const LabelBg = styled.div<{ start?: string; end?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, ${(props) => props.end}, ${(props) => props.start});
  opacity: 0.5;
  z-index: -1;
`;

const Category = styled.div`
  font-size: 12px;
  font-weight: bold;
`;

const StyledSpacer = styled.div`
  flex: 1;
`;

const Name = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding-bottom: 10px;
  font-size: 19px;
  font-weight: bold;
  border-bottom: 1px solid #000;
`;

const Tags = styled.div`
  margin-top: 5px;
  margin-left: -10px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Tag = styled.div`
  margin-top: 5px;
  margin-left: 10px;
  font-size: 12px;
  font-weight: bold;
`;

const Sdgs = styled.div`
  display: flex;
  flex-direction: row;

  & > div {
    display: flex;
    flex-direction: column;
  }

  ${media.mid`
    padding: 20px;
    width: calc(100% - 40px);
  `}
`;

const StyledMainImage = styled.div`
  aspect-ratio: 1 / 1;
  position: relative;
  width: 116px;
`;

const StyledSubImage = styled.div`
  aspect-ratio: 1 / 1;
  position: relative;
  width: 58px;
`;

const Tabs = styled.div`
  margin: auto;
  width: 100%;
  max-width: 1000px;
  height: 85px;
  background: #262626;
  display: flex;
  flex-direction: row;
`;

const Tab = styled.div<{ active?: boolean }>`
  width: 120px;
  height: 81px;
  line-height: 85px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  border-bottom: 4px solid ${(props) => (props.active ? '#fff' : '#262626')};
  cursor: pointer;

  & > span {
    font-size: 12px;
    font-weight: normal;
    margin-left: 5px;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 100px;
`;

const Title = styled.div`
  margin-top: 50px;
  width: 830px;
  font-size: 30px;
  font-weight: bold;
  line-height: 48px;
  letter-spacing: 2px;

  ${media.mid`
    width: calc(100% - 40px);
    padding: 0 20px;
    font-size: 24px;
    line-height: 32px;
    text-align: justify;
  `}
`;

const Description = styled.div`
  margin-top: 50px;
  width: 830px;
  white-space: pre-wrap;
  font-size: 18px;
  line-height: 38px;

  ${media.mid`
    width: calc(100% - 40px);
    padding: 0 20px;
    font-size: 16px;
    line-height: 30px;
    text-align: justify;
  `}
`;

const SlideWrapper = styled.div`
  margin-top: 80px;
  width: 830px;
  padding-top: 622.5px;
  position: relative;

  ${media.mid`
    width: 100%;
    padding-top: 75%;
  `}
`;

const Slide = styled.iframe`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const SlideVideoWrapper = styled.div`
  margin-top: 80px;
  width: 830px;
  position: relative;
  padding-top: 466.875px;

  ${media.mid`
    width: 100%;
    padding-top: 56.25%;
    object-fit: cover;
  `}
`;

const SlideVideo = styled.iframe`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const Comments = styled.div`
  margin-top: 50px;
  width: 830px;

  ${media.mid`
    width: calc(100% - 40px);
    padding: 0 20px;
  `}
`;

const Comment = styled.div`
  padding: 0 35px;
  height: 90px;
  border-radius: 45px;
  background: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;

  ${media.mid`
    padding: 0 15px;
    height: 80px;
  `}
`;

const CommentText = styled.div`
  margin-left: 10px;
  padding-left: 20px;
  flex: 1;
  height: 52px;
  border-radius: 26px;
  background: #ebebeb;
  font-size: 12px;
  line-height: 52px;
  color: #505050;

  ${media.mid`
  `}
`;

const QuestionSection = styled.div`
  margin-top: 80px;
  width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Questions = styled.div`
  padding: 40px 0 80px;
  width: 830px;

  ${media.mid`
    width: calc(100% - 40px);
  `}
`;

const QuestionWrapper = styled.div`
  margin-top: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid #707070;
`;

const Section = styled.div`
  margin-top: 80px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.mid`
    margin-top: 0;
  `}
`;

const SectionTitle = styled.div`
  width: 1000px;
  margin-left: 85px;
  font-size: 40px;
  font-weight: bold;

  & > i {
    font-style: italic;
  }

  & > span {
    font-size: 15px;
    margin-left: 5px;
    font-style: normal;
  }

  ${media.mid`
    width: auto;
    margin-left: 0;
    padding: 30px 0 30px 20px;
    width: calc(100% - 20px);
    font-size: 20px;

    & > i {
      font-size: 35px;
    }

    & > span {
      display: block;
      font-size: 14px;
      margin-top: 5px;
      margin-left: 0;
    }
  `}
`;

const SectionContent = styled.div`
  width: 100%;
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 830px;

  ${media.mid`
    margin-top: 10px;
  `}
`;

const Boxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;

  ${media.mid`
    gap: 10px;
  `}
`;

const BoxWrapper = styled.div`
  flex: 1 1 calc(33.333% - 20px);
  box-sizing: border-box;
  margin-bottom: 20px;
`;

const PresentationLinks = styled.div`
  margin-top: 70px;
  width: 830px;

  ${media.mid`
    width: calc(100% - 40px);
    padding: 0 20px;
  `}
`;

const PresentationLink = styled.a`
  margin-top: 10px;
  color: #213d98;
  text-decoration: underline;
  font-size: 18px;
  line-height: 36px;

  ${media.mid`
    font-size: 16px;
    line-height: 30px;
  `}
`;
